<script lang="ts" setup>
import { breakpointsTailwind } from '@vueuse/core'
import { type Transcription, useAudioPlayer, useComments } from '../logic'

const props = defineProps<{
  tag?: string
  data: Transcription.IContentParagraph

  /** Whether the transcription is paragraphed or not. If false, desktop comments are disabled */
  isParagraphed: boolean
}>()

const id = useId()
const breakpoints = useBreakpoints(breakpointsTailwind)

const comments = useComments()
const entries = computed(() =>
  comments.list.filter(
    entry => entry.seconds >= props.data.start && entry.seconds < props.data.end
  )
)

function updatePlayerTime(seconds: number) {
  const { audio } = useAudioPlayer()
  audio.currentTime = seconds
}

function getStackedCardStyle(length: number, index: number) {
  const offset = Math.min(6, (length - index - 1) * 3)

  return {
    top: `${index * 30}px`,
    width: `${100 - offset}%`,
    marginLeft: `${offset / 2}%`
  }
}
</script>

<template>
  <component
    :is="tag ?? 'p'" class="relative min-w-full" :class="{ inline: !isParagraphed }"
    :style="{ minHeight: comments.enabled ? `${entries.length * 43}px` : undefined }"
  >
    <slot />

    <ClientOnly v-if="comments.enabled && entries.length && breakpoints.xl.value && isParagraphed">
      <section
        class="w-240px absolute h-full top-0 left-full ml-10 [&:has(.active),&:hover]:z-10"
        :style="{ viewTransitionName: `desktop-comments-${id}` }"
      >
        <LazyPageListenCommentsCard
          v-for="(comment, i) in entries"
          :key="i"
          :data="comment"
          class="group comment absolute p-3 rounded-md transition-all bg-$color-background-elevated outline-1 outline-solid outline-transparent cursor-pointer ![&:hover,&:active,&.active]:z-1 ![&:hover,&:active,&.active]:w-full ![&:hover,&:active,&.active]:m-0 ![&:hover,&:active,&.active]:outline-$color-primary"
          :class="{ active: comments.highlightedList.includes(comment) }" message-class="group-not-hover:line-clamp-3 group-hover:-mx-3 group-hover:px-3 group-hover:max-h-20vh group-hover:overflow-y-auto"
          :style="getStackedCardStyle(entries.length, i)"
          @click="updatePlayerTime(comment.seconds)"
        />
      </section>
    </ClientOnly>
  </component>
</template>

<style scoped lang="sass">
.comment
  interpolate-size: allow-keywords
  box-shadow: 0 4px 8px var(--color-neutral-5), 0 -2px 6px rgba(0,0,0,0.05)
</style>
